import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Input,
  Textarea,
  Label,
  Switch,
  MediaPreview,
  cn,
} from "@nexus/ui";
import {
  Plus,
  Trash2,
  Upload,
  X,
  ImageIcon,
  ListChecks,
  Package,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { toast } from "sonner";
import {
  useCreateAdminVendor,
  useReplaceAdminVendor,
  useSetVendorContactEmailApproval,
} from "@/lib/queries";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  presignVendorLogoUpload,
  confirmVendorLogoUpload,
} from "@/features/marketplace/api/vendor-logo-upload.api";
import { putToSignedUrlWithProgress, stageMemoryPreflightUpload } from "@/features/preflight/api/uploads.api";
import {
  getVendorLogoMaxBytes,
  isAllowedVendorLogoType,
  VENDOR_LOGO_ACCEPT,
} from "@/features/marketplace/lib/vendor-logo-upload-constraints";
import { getVendorInitials } from "@/features/marketplace/lib/vendorInitials";
import { isPackageCadence } from "@/features/marketplace/lib/package-cadence";
import { TermsField } from "./TermsField";
import { QuestionEditor } from "./QuestionEditor";
import { PackageEditor } from "./PackageEditor";
import { CollapsibleSection } from "./CollapsibleSection";
import { FormNavRail } from "./FormNavRail";
import { SortableRow, SectionEmpty } from "./FormParts";
import {
  VENDOR_FORM_LIMITS,
  VENDOR_TEXT_LIMITS,
  createEmptyPackage,
  createEmptyQuestion,
  isContactEmailPersisted,
  resolveLegacyContactEmail,
  toEditablePackage,
  toEditableQuestion,
  validateVendorBasics,
  validateVendorContactEmails,
  validateVendorSubEntities,
  SECTION_ID,
  type EditablePackage,
  type EditableQuestion,
  type SectionKey,
  type VendorBasicsField,
  type VendorFieldErrors,
  type VendorContactEmailErrors,
} from "../lib/admin-vendor-form.utils";

import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type { AdminVendorWritePayload } from "@/features/admin/marketplace/types/admin-marketplace.types";

interface AdminVendorFormProps {
  onClose: () => void;
  vendor?: AdminVendorRow;
}

/** Validation order, used to focus the first invalid field on submit. */
const BASICS_FIELD_ORDER: VendorBasicsField[] = [
  "name",
  "slug",
  "websiteUrl",
  "termsMarkdown",
];

const BASICS_FIELD_INPUT_ID: Record<VendorBasicsField, string> = {
  name: "vf-name",
  slug: "vf-slug",
  websiteUrl: "vf-web",
  termsMarkdown: "vf-terms",
};

const CONTACT_EMAIL_COUNT_MAX = 10;

/** A contact address row while it is being edited; `id` is absent until saved. */
interface EditableContactEmail {
  uid: string;
  id?: string;
  email: string;
  approval: "APPROVED" | "PENDING";
  isPrimary: boolean;
  /**
   * The address as currently stored. Approval acts on the persisted row, so an
   * edited-but-unsaved field would approve a different address than the one on
   * screen — the control stays disabled until the two match again.
   */
  persistedEmail?: string;
}

function newContactEmailRow(): EditableContactEmail {
  return { uid: crypto.randomUUID(), email: "", approval: "PENDING", isPrimary: false };
}

/**
 * Seeds the editor from whichever address model the vendor already has. A
 * vendor predating per-address approval carries only the single `contactEmail`,
 * which is shown as its (approved) primary so an editor sees one consistent list.
 */
function toEditableContactEmails(vendor: AdminVendorRow | undefined): EditableContactEmail[] {
  if (vendor?.contactEmails?.length) {
    return vendor.contactEmails.map((row) => ({
      uid: crypto.randomUUID(),
      id: row.id,
      email: row.email,
      approval: row.approval,
      isPrimary: row.isPrimary,
      persistedEmail: row.email,
    }));
  }
  if (vendor?.contactEmail) {
    return [
      {
        uid: crypto.randomUUID(),
        email: vendor.contactEmail,
        approval: vendor.contactEmailApproval,
        isPrimary: true,
        persistedEmail: vendor.contactEmail,
      },
    ];
  }
  return [newContactEmailRow()];
}

/**
 * Builds a dnd-kit drag-end handler that reorders a uid-keyed list in place.
 * Packages and questions share identical reorder logic, differing only by setter.
 */
function makeUidDragEndHandler<T extends { uid: string }>(
  setItems: React.Dispatch<React.SetStateAction<T[]>>,
) {
  return (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((item) => item.uid === active.id);
      const newIndex = prev.findIndex((item) => item.uid === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };
}

/** Prisma Decimal serializes as string over JSON — normalize before submit. */
function normalizePackagePrice(price: number | string | null | undefined): number | null {
  if (price === null || price === undefined || price === "") return null;
  const n = typeof price === "number" ? price : Number(price);
  return Number.isFinite(n) ? n : null;
}

function buildPayload(
  fields: {
    name: string;
    slug: string;
    description: string;
    websiteUrl: string;
    termsMarkdown: string;
    isActive: boolean;
  },
  logoUploadId: string | null,
  packages: EditablePackage[],
  questions: EditableQuestion[],
  contactEmails: EditableContactEmail[],
): AdminVendorWritePayload {
  const addresses = contactEmails
    .map((row) => ({ ...row, email: row.email.trim() }))
    .filter((row) => row.email.length > 0);
  const primary = addresses.find((row) => row.isPrimary) ?? addresses[0];

  return {
    name: fields.name,
    slug: fields.slug.trim(),
    description: fields.description || null,
    logoUploadId: logoUploadId,
    websiteUrl: fields.websiteUrl || null,
    // The legacy single column stays in sync with the primary approved address
    // until nothing reads it (expand/contract).
    contactEmail: resolveLegacyContactEmail(addresses, primary?.uid),
    contactEmails: addresses.map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      email: row.email,
      isPrimary: row.uid === primary?.uid,
    })),
    termsMarkdown: fields.termsMarkdown || null,
    isActive: fields.isActive,
    packages: packages.map(({ uid: _uid, ...p }, i) => ({
      ...p,
      sortOrder: i,
      price: normalizePackagePrice(p.price),
    })),
    questions: questions.map(({ uid: _uid, optionUids: _optionUids, ...q }, i) => ({
      ...q,
      sortOrder: i,
      options: q.type === "SELECT" ? (q.options ?? []).filter(Boolean) : undefined,
      allowMultiple: q.type === "SELECT" ? (q.allowMultiple ?? true) : undefined,
    })),
  };
}

export function AdminVendorForm({ onClose, vendor }: AdminVendorFormProps) {
  const { t } = useTranslation();
  const isEdit = !!vendor;
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [fields, setFields] = useState({
    name: vendor?.name ?? "",
    slug: vendor?.slug ?? "",
    description: vendor?.description ?? "",
    websiteUrl: vendor?.websiteUrl ?? "",
    termsMarkdown: vendor?.termsMarkdown ?? "",
    isActive: vendor?.isActive ?? true,
  });

  const [contactEmails, setContactEmails] = useState<EditableContactEmail[]>(() =>
    toEditableContactEmails(vendor),
  );
  const [contactEmailErrors, setContactEmailErrors] = useState<VendorContactEmailErrors>({
    rows: {},
  });

  /** Upload ID referencing the uploads table — persisted to vendor on save. */
  const [logoUploadId, setLogoUploadId] = useState<string | null>(vendor?.logoUploadId ?? null);
  /** Signed URL from API for an existing vendor logo — display only. */
  const [existingLogoUrl] = useState<string | null>(vendor?.logoUrl ?? null);
  /** Local object URL for the file just picked — for immediate preview. */
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  /** True when the user explicitly removed the logo (clears existingLogoUrl from preview). */
  const [logoRemoved, setLogoRemoved] = useState(false);
  /** 0–100 during PUT to GCS, null when idle. */
  const [logoUploadProgress, setLogoUploadProgress] = useState<number | null>(null);

  // Revoke the object URL when it changes or the sheet unmounts.
  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const displayLogoUrl = logoRemoved ? null : (logoPreviewUrl ?? existingLogoUrl);

  const [packages, setPackages] = useState<EditablePackage[]>(
    vendor?.packages.map((p, i) =>
      toEditablePackage({
        id: p.id,
        name: p.name,
        description: p.description,
        price: p.price != null ? Number(p.price) : null,
        currency: p.currency,
        // Legacy free-text cadences predate the controlled vocabulary; drop the
        // out-of-vocab value to null so the (now strict) enum doesn't 400 an
        // otherwise-unrelated edit. The admin re-picks from the dropdown.
        cadence: isPackageCadence(p.cadence) ? p.cadence : null,
        isPopular: p.isPopular,
        sortOrder: i,
        isActive: p.isActive,
      }),
    ) ?? [],
  );

  const [questions, setQuestions] = useState<EditableQuestion[]>(
    vendor?.questions.map((q, i) =>
      toEditableQuestion({
        id: q.id,
        prompt: q.prompt,
        type: q.type,
        options: q.options,
        allowMultiple: q.allowMultiple,
        isRequired: q.isRequired,
        sortOrder: i,
      }),
    ) ?? [],
  );

  const createMutation = useCreateAdminVendor();
  const replaceMutation = useReplaceAdminVendor();
  const approvalMutation = useSetVendorContactEmailApproval();
  const isPending = createMutation.isPending || replaceMutation.isPending;

  const tenants = useAuth((s) => s.tenants);
  const isSuperTenant = tenants.some((tenant) => tenant.type === "SUPER_TENANT");

  function handleToggleApproval(row: EditableContactEmail, approve: boolean) {
    if (!vendor?.id || !row.id) return;
    approvalMutation.mutate(
      { vendorId: vendor.id, emailId: row.id, approve },
      {
        onSuccess: () =>
          patchContactEmail(row.uid, { approval: approve ? "APPROVED" : "PENDING" }),
      },
    );
  }
  const isLogoUploading = logoUploadProgress !== null;

  const [errors, setErrors] = useState<VendorFieldErrors>({});
  const [touched, setTouched] = useState<Partial<Record<VendorBasicsField, boolean>>>({});
  // Sections that hold validatable fields are controlled so we can re-open them
  // when a collapsed section hides a validation error on submit.
  const [basicsOpen, setBasicsOpen] = useState(true);
  const [agreementOpen, setAgreementOpen] = useState(true);
  const [packagesOpen, setPackagesOpen] = useState(true);
  const [questionsOpen, setQuestionsOpen] = useState(true);
  // Tracks a save attempt so the "needs a package" error only shows after submit.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Nav rail: track which section is in view and jump to sections on click.
  const rootRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>("basics");

  const SECTION_OPENERS: Record<SectionKey, (open: boolean) => void> = {
    basics: setBasicsOpen,
    agreement: setAgreementOpen,
    packages: setPackagesOpen,
    questions: setQuestionsOpen,
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // The app scrolls inside a nested container, so scope the observer to it.
    let scrollRoot: HTMLElement | null = root.parentElement;
    while (scrollRoot) {
      const overflowY = getComputedStyle(scrollRoot).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") break;
      scrollRoot = scrollRoot.parentElement;
    }
    const idToKey = Object.fromEntries(
      Object.entries(SECTION_ID).map(([key, id]) => [id, key]),
    ) as Record<string, SectionKey>;
    const observer = new IntersectionObserver(
      (entries) => {
        // Of the sections crossing the top band, the lowest one (largest top)
        // is the one we've most recently scrolled into.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.boundingClientRect.top - a.boundingClientRect.top)[0];
        const key = visible ? idToKey[visible.target.id] : undefined;
        if (key) setActiveSection(key);
      },
      { root: scrollRoot, rootMargin: "0px 0px -65% 0px", threshold: 0 },
    );
    Object.values(SECTION_ID).forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  function goToSection(key: SectionKey) {
    SECTION_OPENERS[key](true);
    setActiveSection(key);
    requestAnimationFrame(() => {
      document.getElementById(SECTION_ID[key])?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  const packagesError = submitAttempted && packages.length < VENDOR_FORM_LIMITS.packageCountMin;

  // Per-section error flags for the nav rail — only after a save attempt, and
  // mirroring how handleSubmit maps invalid fields back to their sections.
  const hasContactEmailError =
    Boolean(contactEmailErrors.list) || Object.keys(contactEmailErrors.rows).length > 0;

  const sectionErrors: Record<SectionKey, boolean> = {
    basics: submitAttempted && Boolean(errors.name || errors.slug),
    agreement:
      submitAttempted &&
      Boolean(errors.websiteUrl || errors.termsMarkdown || hasContactEmailError),
    packages: packagesError,
    questions: false,
  };

  function setField<K extends keyof typeof fields>(key: K, value: (typeof fields)[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
    // Keep errors current on every keystroke; `fieldError` decides when to show them.
    setErrors(validateVendorBasics({ ...fields, [key]: value }));
  }

  function handleFieldBlur(field: VendorBasicsField) {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validateVendorBasics(fields));
  }

  /**
   * Show a field's error after it's been touched or submit attempted. The
   * optional format fields (website, email) validate eagerly — as soon as they
   * hold a non-empty, malformed value — so typos surface without leaving the field.
   */
  function fieldError(field: VendorBasicsField): string | undefined {
    const isEagerFormatField = field === "websiteUrl";
    const hasValue = fields[field].trim().length > 0;
    const shouldShow = submitAttempted || touched[field] || (isEagerFormatField && hasValue);
    return shouldShow ? errors[field] : undefined;
  }

  function patchContactEmail(uid: string, patch: Partial<EditableContactEmail>) {
    setContactEmails((prev) => {
      const next = prev.map((row) => (row.uid === uid ? { ...row, ...patch } : row));
      setContactEmailErrors(validateVendorContactEmails(next));
      return next;
    });
  }

  function addContactEmail() {
    setContactEmails((prev) =>
      prev.length >= CONTACT_EMAIL_COUNT_MAX ? prev : [...prev, newContactEmailRow()],
    );
  }

  function removeContactEmail(uid: string) {
    setContactEmails((prev) => {
      const next = prev.filter((row) => row.uid !== uid);
      const result = next.length > 0 ? next : [newContactEmailRow()];
      setContactEmailErrors(validateVendorContactEmails(result));
      return result;
    });
  }

  /** Exactly one primary, mirroring the single-primary rule the API enforces. */
  function setPrimaryContactEmail(uid: string) {
    setContactEmails((prev) => prev.map((row) => ({ ...row, isPrimary: row.uid === uid })));
  }

  const termsDirty = fields.termsMarkdown !== (vendor?.termsMarkdown ?? "");

  function patchPackage(idx: number, patch: Partial<EditablePackage>) {
    setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }

  function patchQuestion(idx: number, patch: Partial<EditableQuestion>) {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }

  // Only one package can be Popular; clicking the current one clears it.
  function togglePopular(idx: number) {
    setPackages((prev) =>
      prev.map((p, i) => ({ ...p, isPopular: i === idx ? !p.isPopular : false })),
    );
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handlePackageDragEnd = makeUidDragEndHandler(setPackages);
  const handleQuestionDragEnd = makeUidDragEndHandler(setQuestions);

  async function handleLogoFileChange(file: File | undefined) {
    if (!file || !activeTenantId) return;

    if (!isAllowedVendorLogoType(file.type)) {
      toast.error(t("marketplace.admin.form.logoInvalidType"));
      return;
    }

    const maxBytes = getVendorLogoMaxBytes(file.type);
    if (maxBytes != null && file.size > maxBytes) {
      toast.error(t("marketplace.admin.form.logoTooLarge"));
      return;
    }

    // Show local preview immediately — no waiting for the upload to finish.
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setLogoRemoved(false);
    setLogoUploadProgress(0);

    try {
      const presign = await presignVendorLogoUpload({
        tenantId: activeTenantId,
        contentType: file.type,
        sizeBytes: file.size,
        originalName: file.name,
      });

      if (presign.requiresMemoryStage || presign.requiresServerStage) {
        await stageMemoryPreflightUpload({
          tenantId: activeTenantId,
          uploadId: presign.uploadId,
          contentType: file.type,
          file,
        });
        setLogoUploadProgress(100);
      } else {
        await putToSignedUrlWithProgress(presign.uploadUrl, file, presign.requiredHeaders, {
          onProgress: setLogoUploadProgress,
        });
      }

      const confirmed = await confirmVendorLogoUpload({
        tenantId: activeTenantId,
        uploadId: presign.uploadId,
      });

      setLogoUploadId(confirmed.uploadId);
      toast.success(t("marketplace.admin.form.logoUploaded"));
    } catch {
      toast.error(t("marketplace.admin.form.logoUploadFailed"));
      // Revert preview on failure.
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl(null);
    } finally {
      setLogoUploadProgress(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  }

  function handleLogoRemove() {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(null);
    setLogoUploadId(null);
    setLogoRemoved(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    setSubmitAttempted(true);
    const nextErrors = validateVendorBasics(fields);
    const nextEmailErrors = validateVendorContactEmails(contactEmails);
    const hasEmailError =
      Boolean(nextEmailErrors.list) || Object.keys(nextEmailErrors.rows).length > 0;
    const hasPackagesError = packages.length < VENDOR_FORM_LIMITS.packageCountMin;

    if (Object.keys(nextErrors).length > 0 || hasEmailError || hasPackagesError) {
      setErrors(nextErrors);
      setContactEmailErrors(nextEmailErrors);
      setTouched({
        name: true,
        slug: true,
        websiteUrl: true,
        termsMarkdown: true,
      });
      // Re-open any collapsed section that hides an invalid field so the error is visible.
      if (nextErrors.name || nextErrors.slug) setBasicsOpen(true);
      if (nextErrors.websiteUrl || nextErrors.termsMarkdown || hasEmailError) {
        setAgreementOpen(true);
      }
      if (hasPackagesError) setPackagesOpen(true);

      const firstInvalid = BASICS_FIELD_ORDER.find((field) => nextErrors[field]);
      const firstInvalidEmailUid = contactEmails.find((row) => nextEmailErrors.rows[row.uid])?.uid;
      // Focus after the re-render so a just-expanded control can receive focus and scroll into view.
      requestAnimationFrame(() => {
        const targetId = firstInvalid
          ? BASICS_FIELD_INPUT_ID[firstInvalid]
          : firstInvalidEmailUid
            ? `vf-email-${firstInvalidEmailUid}`
            : hasPackagesError
              ? "vf-add-package"
              : undefined;
        if (targetId) document.getElementById(targetId)?.focus();
      });

      toast.error(t("marketplace.admin.form.errors.fixErrors"));
      return;
    }

    // Validate packages/SELECT-question contents the way the API will, so a
    // stripped-empty or duplicate option doesn't bounce back as an opaque 400.
    const subEntityError = validateVendorSubEntities(packages, questions);
    if (subEntityError) {
      SECTION_OPENERS[subEntityError.section](true);
      toast.error(
        t(`marketplace.admin.form.errors.${subEntityError.messageKey}`, subEntityError.values),
      );
      return;
    }

    const payload = buildPayload(fields, logoUploadId, packages, questions, contactEmails);
    try {
      if (isEdit) {
        await replaceMutation.mutateAsync({ vendorId: vendor.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
    } catch {
      return;
    }
    onClose();
  }

  return (
    <div
      ref={rootRef}
      className="lg:grid lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-start lg:gap-10"
    >
      <FormNavRail
        logoUrl={displayLogoUrl}
        name={fields.name}
        slug={fields.slug}
        isActive={fields.isActive}
        isEdit={isEdit}
        activeSection={activeSection}
        packagesCount={packages.length}
        questionsCount={questions.length}
        sectionErrors={sectionErrors}
        onNavigate={goToSection}
      />

      <div className="flex min-w-0 flex-col gap-6">
        <form
          id="vendor-form"
          noValidate
          onSubmit={(e) => void handleSubmit(e)}
          className="flex flex-col gap-6"
        >
          {/* ─── Basics ─── */}
          <CollapsibleSection
            id={SECTION_ID.basics}
            step={1}
            title={t("marketplace.admin.sections.basics")}
            hint={t("marketplace.admin.sections.basicsHint")}
            open={basicsOpen}
            onOpenChange={setBasicsOpen}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-name" className="flex items-center gap-0.5">
                {t("marketplace.admin.form.name")}
                <span className="text-sm text-destructive" aria-hidden>
                  *
                </span>
              </Label>
              <Input
                id="vf-name"
                required
                maxLength={VENDOR_TEXT_LIMITS.nameMax}
                value={fields.name}
                onChange={(e) => setField("name", e.target.value)}
                onBlur={() => handleFieldBlur("name")}
                aria-invalid={fieldError("name") ? true : undefined}
                aria-describedby={fieldError("name") ? "vf-name-error" : undefined}
              />
              {fieldError("name") && (
                <p id="vf-name-error" role="alert" className="text-xs text-destructive">
                  {t(fieldError("name")!)}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-slug" className="flex items-center gap-0.5">
                {t("marketplace.admin.form.slug")}
                <span className="text-sm text-destructive" aria-hidden>
                  *
                </span>
              </Label>
              <Input
                id="vf-slug"
                required
                maxLength={VENDOR_TEXT_LIMITS.nameMax}
                value={fields.slug}
                onChange={(e) => setField("slug", e.target.value.toLowerCase())}
                onBlur={() => handleFieldBlur("slug")}
                aria-invalid={fieldError("slug") ? true : undefined}
                aria-describedby={
                  fieldError("slug") ? "vf-slug-hint vf-slug-error" : "vf-slug-hint"
                }
              />
              <p id="vf-slug-hint" className="text-xs text-muted-foreground">
                {t("marketplace.admin.form.slugHint", "Must be unique across all partners")}
              </p>
              {fieldError("slug") && (
                <p id="vf-slug-error" role="alert" className="text-xs text-destructive">
                  {t(fieldError("slug")!)}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="vf-desc">{t("marketplace.admin.form.description")}</Label>
                {fields.description.length >= VENDOR_TEXT_LIMITS.descriptionMax * 0.9 && (
                  <span className="shrink-0 text-xs tabular-nums text-status-warning">
                    {fields.description.length}/{VENDOR_TEXT_LIMITS.descriptionMax}
                  </span>
                )}
              </div>
              <Textarea
                id="vf-desc"
                rows={2}
                maxLength={VENDOR_TEXT_LIMITS.descriptionMax}
                value={fields.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-logo">{t("marketplace.admin.form.logo")}</Label>
              <div className="flex items-start gap-3">
                {/* Thumbnail preview */}
                <div className="relative shrink-0">
                  {displayLogoUrl ? (
                    <MediaPreview
                      src={displayLogoUrl}
                      thumbnailSrc={displayLogoUrl}
                      type="image"
                      alt={fields.name || "Logo"}
                      title={fields.name || "Logo"}
                      className="h-14 w-14 rounded-lg border border-border/60 bg-background object-contain"
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      className="flex h-14 w-14 items-center justify-center rounded-lg border border-border/60 bg-muted text-sm font-semibold text-muted-foreground"
                    >
                      {fields.name ? getVendorInitials(fields.name) : <ImageIcon className="h-5 w-5 opacity-40" />}
                    </div>
                  )}
                  {/* Upload progress ring overlay */}
                  {isLogoUploading && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/80">
                      <span className="text-[10px] font-semibold tabular-nums text-foreground">
                        {logoUploadProgress ?? 0}%
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={logoInputRef}
                      id="vf-logo"
                      type="file"
                      accept={VENDOR_LOGO_ACCEPT}
                      className="sr-only"
                      disabled={isLogoUploading || isPending || !activeTenantId}
                      onChange={(e) => void handleLogoFileChange(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isLogoUploading || isPending || !activeTenantId}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                      {isLogoUploading
                        ? t("marketplace.admin.form.logoUploading")
                        : t("marketplace.admin.form.logoUpload")}
                    </Button>
                    {(displayLogoUrl || logoUploadId) && !isLogoUploading && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={isPending}
                        onClick={handleLogoRemove}
                      >
                        <X className="mr-1.5 h-3.5 w-3.5" />
                        {t("marketplace.admin.form.logoRemove")}
                      </Button>
                    )}
                  </div>

                  {/* Progress bar */}
                  {isLogoUploading && (
                    <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn(
                          "h-full rounded-full bg-primary transition-all duration-200",
                        )}
                        style={{ width: `${logoUploadProgress ?? 0}%` }}
                      />
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    {t("marketplace.admin.form.logoHint")}
                  </p>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* ─── Agreement & contact ─── */}
          <CollapsibleSection
            id={SECTION_ID.agreement}
            step={2}
            title={t("marketplace.admin.sections.agreement")}
            hint={t("marketplace.admin.sections.agreementHint")}
            open={agreementOpen}
            onOpenChange={setAgreementOpen}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-web">{t("marketplace.admin.form.websiteUrl")}</Label>
              <Input
                id="vf-web"
                type="url"
                inputMode="url"
                maxLength={VENDOR_TEXT_LIMITS.websiteUrlMax}
                placeholder="https://example.com"
                value={fields.websiteUrl}
                onChange={(e) => setField("websiteUrl", e.target.value)}
                onBlur={() => handleFieldBlur("websiteUrl")}
                aria-invalid={fieldError("websiteUrl") ? true : undefined}
                aria-describedby={fieldError("websiteUrl") ? "vf-web-error" : undefined}
              />
              {fieldError("websiteUrl") && (
                <p id="vf-web-error" role="alert" className="text-xs text-destructive">
                  {t(fieldError("websiteUrl")!)}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <Label className="flex items-center gap-0.5">
                  {t("marketplace.admin.form.contactEmails")}
                  <span className="text-sm text-destructive" aria-hidden>
                    *
                  </span>
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("marketplace.admin.form.contactEmailsHint")}
                </p>
              </div>

              {contactEmails.map((row, index) => {
                const rowError = contactEmailErrors.rows[row.uid];
                const inputId = `vf-email-${row.uid}`;
                const isApproved = row.approval === "APPROVED";
                const isUnsaved = !isContactEmailPersisted(row);
                return (
                  <div key={row.uid} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Input
                        id={inputId}
                        type="email"
                        inputMode="email"
                        maxLength={VENDOR_TEXT_LIMITS.contactEmailMax}
                        placeholder="vendor@example.com"
                        value={row.email}
                        onChange={(e) => patchContactEmail(row.uid, { email: e.target.value })}
                        aria-invalid={rowError ? true : undefined}
                        aria-describedby={rowError ? `${inputId}-error` : undefined}
                        aria-label={t("marketplace.admin.form.contactEmailRowLabel", {
                          index: index + 1,
                        })}
                      />
                      <Badge variant={isApproved ? "secondary" : "outline"}>
                        {t(
                          isApproved
                            ? "marketplace.admin.form.emailApproved"
                            : "marketplace.admin.form.emailPending",
                        )}
                      </Badge>
                      {isSuperTenant && (
                        <Button
                          type="button"
                          variant={isApproved ? "ghost" : "outline"}
                          size="sm"
                          className="shrink-0"
                          disabled={isUnsaved || approvalMutation.isPending}
                          title={
                            isUnsaved
                              ? t("marketplace.admin.form.approveNeedsSave")
                              : undefined
                          }
                          onClick={() => handleToggleApproval(row, !isApproved)}
                        >
                          {t(
                            isApproved
                              ? "marketplace.admin.form.revokeEmail"
                              : "marketplace.admin.form.approveEmail",
                          )}
                        </Button>
                      )}
                      <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                        <input
                          type="radio"
                          name="vf-primary-email"
                          checked={row.isPrimary}
                          onChange={() => setPrimaryContactEmail(row.uid)}
                          aria-label={t("marketplace.admin.form.setPrimaryEmail", {
                            email: row.email || t("marketplace.admin.form.contactEmails"),
                          })}
                        />
                        {t("marketplace.admin.form.primaryEmail")}
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeContactEmail(row.uid)}
                        aria-label={t("marketplace.admin.form.removeEmail", {
                          email: row.email,
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {rowError && (
                      <p id={`${inputId}-error`} role="alert" className="text-xs text-destructive">
                        {t(rowError)}
                      </p>
                    )}
                  </div>
                );
              })}

              {contactEmailErrors.list && (
                <p role="alert" className="text-xs text-destructive">
                  {t(contactEmailErrors.list)}
                </p>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                disabled={contactEmails.length >= CONTACT_EMAIL_COUNT_MAX}
                onClick={addContactEmail}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("marketplace.admin.form.addContactEmail")}
              </Button>
            </div>

            <TermsField
              value={fields.termsMarkdown}
              onChange={(v) => setField("termsMarkdown", v)}
              onBlur={() => handleFieldBlur("termsMarkdown")}
              currentVersion={isEdit ? vendor?.termsVersion : undefined}
              isDirty={termsDirty}
              disabled={isPending}
              error={fieldError("termsMarkdown") ? t(fieldError("termsMarkdown")!) : undefined}
            />

            <label className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
              <span className="flex flex-col">
                <span className="text-sm font-medium">
                  {t("marketplace.admin.form.isActive")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("marketplace.admin.form.isActiveHint", {
                    defaultValue: "Visible to tenants in the catalog.",
                  })}
                </span>
              </span>
              <Switch
                id="vf-active"
                checked={fields.isActive}
                onCheckedChange={(v) => setField("isActive", v)}
              />
            </label>
          </CollapsibleSection>

          {/* ─── Packages ─── */}
          <CollapsibleSection
            id={SECTION_ID.packages}
            step={3}
            title={t("marketplace.admin.form.packages")}
            hint={t("marketplace.admin.sections.packagesHint", {
              defaultValue: "Pricing tiers shown on the vendor's catalog card.",
            })}
            count={packages.length}
            open={packagesOpen}
            onOpenChange={setPackagesOpen}
            actions={
              <Button
                id="vf-add-package"
                type="button"
                variant="outline"
                size="sm"
                disabled={packages.length >= VENDOR_FORM_LIMITS.packageCountMax}
                onClick={() => setPackages((prev) => [...prev, createEmptyPackage()])}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t("marketplace.admin.form.addPackage")}
              </Button>
            }
          >
            {packages.length === 0 ? (
              <div className="flex flex-col gap-2">
                <SectionEmpty
                  icon={<Package className="h-5 w-5 text-muted-foreground/60" />}
                  label={t("marketplace.admin.form.packagesEmpty", {
                    defaultValue: "No packages yet. Add one to show pricing on the card.",
                  })}
                  isError={packagesError}
                />
                {packagesError && (
                  <p role="alert" className="text-xs text-destructive">
                    {t("marketplace.admin.form.errors.packagesMin")}
                  </p>
                )}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handlePackageDragEnd}
              >
                <SortableContext
                  items={packages.map((p) => p.uid)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col divide-y divide-border/60">
                    {packages.map((pkg, idx) => (
                      <SortableRow key={pkg.uid} id={pkg.uid}>
                        {(dragHandle) => (
                          <PackageEditor
                            pkg={pkg}
                            index={idx}
                            onChange={(patch) => patchPackage(idx, patch)}
                            onRemove={() =>
                              setPackages((prev) => prev.filter((_, i) => i !== idx))
                            }
                            onMarkPopular={() => togglePopular(idx)}
                            dragHandle={dragHandle}
                          />
                        )}
                      </SortableRow>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CollapsibleSection>

          {/* ─── Questions ─── */}
          <CollapsibleSection
            id={SECTION_ID.questions}
            step={4}
            title={t("marketplace.admin.form.questions")}
            hint={t("marketplace.admin.sections.questionsHint", {
              defaultValue:
                "Onboarding questions tenants answer when connecting. Mix short, long, and multiple-choice answers.",
            })}
            count={questions.length}
            open={questionsOpen}
            onOpenChange={setQuestionsOpen}
            actions={
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={questions.length >= VENDOR_FORM_LIMITS.questionCountMax}
                onClick={() => setQuestions((prev) => [...prev, createEmptyQuestion()])}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t("marketplace.admin.form.addQuestion")}
              </Button>
            }
          >
            {questions.length === 0 ? (
              <SectionEmpty
                icon={<ListChecks className="h-5 w-5 text-muted-foreground/60" />}
                label={t("marketplace.admin.form.questionsEmpty", {
                  defaultValue: "No onboarding questions yet. Add your first question.",
                })}
              />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleQuestionDragEnd}
              >
                <SortableContext
                  items={questions.map((q) => q.uid)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col divide-y divide-border/60">
                    {questions.map((q, idx) => (
                      <SortableRow key={q.uid} id={q.uid}>
                        {(dragHandle) => (
                          <QuestionEditor
                            question={q}
                            index={idx}
                            onChange={(patch) => patchQuestion(idx, patch)}
                            onRemove={() =>
                              setQuestions((prev) => prev.filter((_, i) => i !== idx))
                            }
                            dragHandle={dragHandle}
                          />
                        )}
                      </SortableRow>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CollapsibleSection>
        </form>

        <div className="sticky bottom-0 z-10 flex items-center gap-3 rounded-xl border border-border bg-background/85 px-4 py-3 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <p className="hidden text-xs text-muted-foreground sm:block">
            <span className="text-destructive" aria-hidden>
              *
            </span>{" "}
            {t("marketplace.admin.form.requiredHint", { defaultValue: "Required fields" })}
          </p>
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t("marketplace.admin.form.cancel")}
            </Button>
            <Button type="submit" form="vendor-form" disabled={isPending}>
              {isPending
                ? isEdit
                  ? t("marketplace.admin.form.saving")
                  : t("marketplace.admin.form.creating")
                : isEdit
                  ? t("marketplace.admin.form.save")
                  : t("marketplace.admin.form.create")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

